import { useAuth } from '@/hooks/useAuth'
import { Bell, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'

type Props = {
  onRefresh: () => void
  isRefreshing: boolean
}

export function FeedHeader({ onRefresh, isRefreshing }: Props) {
  const { profile } = useAuth()

  const showVerificationBanner = profile?.verification_status === 'pending_verification'
  const showProfileBanner = profile?.is_profile_complete === false
  const greeting = getGreeting()

  return (
    <div className="sticky top-0 bg-background/95 backdrop-blur z-10 border-b">
      <div className="px-4 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">
              {greeting}
              {profile?.first_name && profile.first_name !== '(Pending)'
                ? `, ${profile.first_name}`
                : ''}
            </h1>
            <p className="text-xs text-muted-foreground">News & announcements for your area</p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label="Refresh feed"
            >
              <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Profile completion nudge */}
        {showProfileBanner && (
          <Link
            to="/profile/personal"
            className="block bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg p-3 text-sm"
          >
            <div className="flex items-start gap-2">
              <Bell className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-200">
                  Complete your profile
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  Add your name and personal info to get verified.
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Verification status banner */}
        {showVerificationBanner && !showProfileBanner && (
          <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-lg p-3 text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-200">Verification pending</p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
              Your barangay is reviewing your registration.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}
